import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { PaginationTable, TableWrapper } from 'components/Global';
import LoadingSpinner from 'components/Global/LoadingSpinner';
import { ReactComponent as History } from 'icons/history.svg';
import { useEffect } from 'react';
import { getReferralHistory } from 'features/influencer/actions';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { indonesianDateFormat } from 'utils/helpers';
import { useAppContext } from 'components/Context/AppContext';
import { fetchOrderById } from 'features/orders/actions';
import { id } from 'date-fns/locale';

const historyColumn = [
  { header: 'No.', value: 'no' },
  { header: 'Nama Lengkap', value: 'customer_name' },
  { header: 'Kode Order', value: 'order_code' },
  { header: 'Tanggal Redeem', value: 'redeemed_at' },
];

const ReferralHistory = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { influencerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page')) || 1;
  const keyword = searchParams.get('keyword') || '';

  const { showToast, setShowSpinner } = useAppContext();

  const { referralHistory, historyLoading } = useSelector((state) => state.influencer);

  const [searchTerm, setSearchTerm] = useState(searchParams.get('keyword') || '');
  const [debouncedKeyword, setDebouncedKeyword] = useState(searchTerm);

  useEffect(() => {
    if (!influencerId) return;
    dispatch(getReferralHistory({ id: influencerId, page, keyword }));
  }, [influencerId, page, keyword]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedKeyword(searchTerm);
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);

    // Update filters
    if (debouncedKeyword) {
      newParams.set('keyword', debouncedKeyword);
    } else {
      newParams.delete('keyword');
    }

    setSearchParams(newParams);
  }, [debouncedKeyword]);

  const getURLDestination = async (transaction_key) => {
    const data = await dispatch(fetchOrderById(transaction_key)).unwrap();

    let orderType = '';

    if (data?.order_type_id === 1) {
      if (data?.order_detail.without_driver) {
        orderType = 'without-driver';
      } else {
        orderType = 'with-driver';
      }
    } else if (data?.order_type_id === 2) {
      orderType = 'airport-transfer';
    }

    return orderType;
  };

  const handleNavigate = async (transactionKey, customerId) => {
    if (!transactionKey) return;

    try {
      setShowSpinner(true);
      const urlDestination = await getURLDestination(transactionKey);

      if (!urlDestination) {
        showToast({ type: 'error', message: 'Jenis order tidak dikenali' });
        return;
      }

      navigate({
        pathname: `/${urlDestination}/detail/${transactionKey}`,
        search: createSearchParams({
          customerId: customerId,
        }).toString(),
      });
    } catch {
      showToast({ type: 'error', message: 'Gagal Mendapatkan Data Order' });
    } finally {
      setShowSpinner(false);
    }
  };

  const handlePageChange = (newPage) => {
    const updatedParams = Object.fromEntries(searchParams.entries());
    updatedParams.page = newPage;
    setSearchParams(updatedParams);
  };

  const pageNumber = (page, idx) => (page > 1 ? (page - 1) * 10 + idx : idx);

  const referralHistoryData = referralHistory?.data?.map((item) => ({
    ...item,
    redeemed_at: formatUtcToLocal(item.redeemed_at),
  }));

  const rows = 10 - referralHistoryData?.length;

  return (
    <div className="influencer__history">
      <TableWrapper
        title="History Redeem Referral"
        icon={<History fill="#FF9900" width="25px" height="25px" />}
        className="influencer-table-wrapper"
      >
        <div className="review-filter-group filter-order">
          <div className="input-with-icon">
            <input
              type="text"
              placeholder="Cari Data..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="icon">
              <img src="/assets/icon/search-icon.svg" className="search-icon" alt="search icon" />
            </span>
          </div>
        </div>
        {historyLoading ? (
          <LoadingSpinner />
        ) : referralHistory?.data?.length === 0 ? (
          <div className="error-state">
            <p>Data history tidak ditemukan.</p>
          </div>
        ) : (
          <>
            <div className="table">
              <table className="table-wrapper">
                <thead className="table-head">
                  <tr>
                    {historyColumn.map((item, i) => (
                      <th key={i}>{item.header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="table-body">
                  {referralHistoryData?.length > 0 &&
                    referralHistoryData.map((item, no) => (
                      <tr key={no}>
                        {historyColumn.map((col, idx) => {
                          if (col.value === 'no') {
                            return (
                              <td
                                key={idx}
                                data-type="no"
                                className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                              >
                                {pageNumber(page, no + 1)}
                              </td>
                            );
                          } else if (col.value === 'order_code') {
                            return (
                              <td
                                key={idx}
                                data-type={'order_code'}
                                className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                                data-key={item.id}
                              >
                                <div
                                  role="button"
                                  className="order-transaction-key"
                                  onClick={() => handleNavigate(item?.order_transaction_key, item.customer_id)}
                                >
                                  {item[col.value]}
                                </div>
                              </td>
                            );
                          } else if (col.value === 'date' || col.value === 'order_date') {
                            return (
                              <td
                                key={idx}
                                data-type={'date'}
                                className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                                data-key={item.id}
                              >
                                {item[col.value] ? indonesianDateFormat(item[col.value]) : '-'}
                              </td>
                            );
                          } else {
                            return (
                              <td
                                key={idx}
                                data-type={col.value}
                                className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                              >
                                {item[col.value]}
                              </td>
                            );
                          }
                        })}
                      </tr>
                    ))}
                  {rows > 0 &&
                    [...Array(rows)].map((_, idx) => (
                      <tr key={idx}>
                        {historyColumn.map((_, idx) => (
                          <td key={idx}></td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <PaginationTable
              currentPage={page}
              onPageChange={handlePageChange}
              totalCount={referralHistory?.pagination?.total}
            />
          </>
        )}
      </TableWrapper>
    </div>
  );
};

export default ReferralHistory;

function formatUtcToLocal(dateStringUtc) {
  // Parse "2025-09-26 04:28:23" as UTC
  const utcDate = new Date(dateStringUtc.replace(' ', 'T') + 'Z');

  // Detect timezone
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const tzMap = {
    'Asia/Jakarta': 'WIB',
    'Asia/Makassar': 'WITA',
    'Asia/Jayapura': 'WIT',
  };
  const tzAbbr = tzMap[timeZone] || timeZone;

  // Format directly → Date knows how to show local time
  const formatted = format(utcDate, 'dd MMM yyyy, HH:mm:ss', { locale: id });

  return `${formatted} ${tzAbbr}`;
}
